import {
  fileLabel,
  fmtDayLong,
  fmtFull,
  fmtTime,
  formatBytes,
  groupByDay,
  zoneAbbr,
  zoneLabel,
  zoneName,
} from '@concord/domain';
import { Button, Notice, Panel, PanelHead, PublicShell } from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { ApiError, api, errorMessage } from '../../lib/api';
import s from './respond.module.css';

interface View {
  state:
    | 'open'
    | 'accepted'
    | 'declined'
    | 'alternative_requested'
    | 'missed_out'
    | 'expired'
    | 'withdrawn'
    | 'closed';
  physician: { firstName: string; lastName: string; title: string; timezone: string; city: string | null };
  navigatorName: string;
  expiresAt: string | null;
  acceptedStartsAt: string | null;
  patientTimezone: string;
  case: {
    reference: string;
    caseType: string | null;
    specialty: string | null;
    patient: string;
    panelSize: number;
    durationMinutes: number;
    history: string;
    questions: string[];
    records: { id: string; description: string; contentType: string; sizeBytes: number }[];
  };
  provisional: { startsAt: string; state: string } | null;
  times: string[];
  terms: string;
  /** The engagement terms version shown; sent back on accept so it is the one recorded. */
  termsVersion: string;
}

const ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
];

/**
 * The physician's secure-link page (MAT-16, SCH-05, SCH-06, MAT-11): everything needed to
 * decide, answered without an account. Physician accounts arrive in Phase 3.
 */
export function RespondPage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['respond', token],
    queryFn: () => api<View>(`/respond/${token}`),
    retry: false,
  });
  const [picked, setPicked] = useState<string | null>(null);
  const [tz, setTz] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [alt, setAlt] = useState('');
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const done = () => qc.invalidateQueries({ queryKey: ['respond', token] });
  const accept = useMutation({
    mutationFn: (startsAt: string) =>
      api(`/respond/${token}/accept`, { body: { startsAt, termsVersion: q.data?.termsVersion } }),
    onSuccess: done,
  });
  const decline = useMutation({
    mutationFn: () => api(`/respond/${token}/decline`, { body: { reason: reason || null } }),
    onSuccess: done,
  });
  const alternative = useMutation({
    mutationFn: () => api(`/respond/${token}/alternative`, { body: { text: alt } }),
    onSuccess: done,
  });

  if (q.isPending) {
    return (
      <PublicShell>
        <div className="skeleton" style={{ height: 320 }} />
      </PublicShell>
    );
  }
  if (q.isError || !q.data) {
    const gone = q.error instanceof ApiError && q.error.status === 404;
    return (
      <PublicShell narrow>
        <Panel>
          <div className={s.state}>
            <b>{gone ? 'This link is not valid.' : 'Something went wrong.'}</b>{' '}
            {gone
              ? 'Check that you opened the full link from your email, or contact the care navigator who invited you.'
              : errorMessage(q.error)}
          </div>
        </Panel>
      </PublicShell>
    );
  }
  const v = q.data;
  const zone = tz ?? v.physician.timezone;
  const pt = v.patientTimezone;
  const busy = accept.isPending || decline.isPending || alternative.isPending;
  const error = accept.error ?? decline.error ?? alternative.error;
  const days = groupByDay(v.times, (t) => new Date(t), zone);
  const chosen = picked ?? (v.provisional ? v.provisional.startsAt : null);
  const hoursLeft = v.expiresAt
    ? Math.max(0, Math.round((new Date(v.expiresAt).getTime() - Date.now()) / 3_600_000))
    : null;
  const size = v.case.panelSize;

  const closed = (title: string, text: string) => (
    <Panel>
      <PanelHead title={title} />
      <div className={s.state}>{text}</div>
    </Panel>
  );

  return (
    <PublicShell>
      <div className={s.hd}>
        <div>
          <h1>
            {v.state === 'open'
              ? `${v.physician.title}, a case is waiting for your answer`
              : `${v.physician.title}, thank you`}
          </h1>
          <p>
            {v.navigatorName} at Cancer Expert Now has invited you to a recorded {v.case.durationMinutes}
            -minute video consultation.
            {v.state === 'open'
              ? ' Everything you need to decide is on this page. No account or sign-in is needed.'
              : ''}
          </p>
        </div>
        {v.state === 'open' && v.expiresAt && (
          <div className={s.expiry}>
            <span className={s.v}>
              Respond by {fmtDayLong(new Date(v.expiresAt), zone)}, {fmtTime(new Date(v.expiresAt), zone)}{' '}
              {zoneAbbr(zone, new Date(v.expiresAt))}
            </span>
            <span className={s.k}>About {hoursLeft} hours from now · after that the link closes</span>
          </div>
        )}
      </div>

      <div className={s.grid}>
        <div className={s.col}>
          {v.case.history ? (
            <Panel aria-label="The case">
              <PanelHead title="The case" sub={<span className="ref">{v.case.reference}</span>} />
              <div className={s.facts}>
                <div>
                  <span className="key">Case type</span>
                  <span className={s.v}>
                    {v.case.caseType}
                    {v.case.specialty && <span className="sub2">{v.case.specialty}</span>}
                  </span>
                </div>
                <div>
                  <span className="key">Patient</span>
                  <span className={s.v}>
                    {v.case.patient}
                    <span className="sub2">
                      {v.case.patient === 'Not given' ? 'Declined at intake' : 'Volunteered at intake'}
                    </span>
                  </span>
                </div>
                <div>
                  <span className="key">Panel</span>
                  <span className={s.v}>
                    {size} {size === 1 ? 'physician' : 'physicians'}
                    <span className="sub2">You would be one of them</span>
                  </span>
                </div>
                <div>
                  <span className="key">Format</span>
                  <span className={s.v}>
                    Video · {v.case.durationMinutes} min<span className="sub2">Recorded · educational</span>
                  </span>
                </div>
              </div>
              <div className={s.prose}>
                <h3>History, in the patient's words</h3>
                {v.case.history
                  .split(/\n{2,}/)
                  .filter(Boolean)
                  .map((p) => (
                    <p key={p.slice(0, 40)}>{p}</p>
                  ))}
                <h3 style={{ marginTop: '1.1rem' }}>Questions for the panel</h3>
                <ol className={s.qs}>
                  {v.case.questions.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ol>
              </div>
            </Panel>
          ) : (
            closed(
              'The case',
              'The case details are available only while the invitation is open or after you accept.',
            )
          )}

          {v.case.records.length > 0 && (
            <Panel aria-label="Records">
              <PanelHead
                title="Records"
                sub={`${v.case.records.length} ${v.case.records.length === 1 ? 'file' : 'files'} · ${formatBytes(v.case.records.reduce((a, r) => a + r.sizeBytes, 0))}`}
              />
              {v.case.records.map((r) => (
                <div key={r.id} className={s.file}>
                  <span className={s.ic}>{fileLabel(r.description, r.contentType)}</span>
                  <div className={s.b}>
                    <div className={s.n1}>{r.description}</div>
                    <div className={s.n2}>
                      {fileLabel(r.description, r.contentType)} · {formatBytes(r.sizeBytes)}
                    </div>
                  </div>
                  <a
                    className={s.dl}
                    href={`/api/respond/${token}/records/${r.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </a>
                </div>
              ))}
              <div className={s.panelnote}>
                <span className={s.g} />
                <span>
                  Viewing or downloading a record is recorded on the case with your name and the time.
                </span>
              </div>
            </Panel>
          )}
        </div>

        <div className={s.col}>
          {v.state === 'open' ? (
            <Panel aria-label="Choose a time">
              <PanelHead
                title={v.provisional ? 'Join at this time' : 'Pick a time that works'}
                sub={`${v.times.length} offered`}
              />
              <div className={s.tzline}>
                <span>
                  Shown in <b>{zoneLabel(zone)}</b>.{' '}
                  {pt !== zone ? `The patient offered these in ${zoneName(pt).replace(' Time', '')}.` : ''}
                </span>
                {changing ? (
                  <select
                    aria-label="Time zone"
                    value={zone}
                    onChange={(e) => {
                      setTz(e.target.value);
                      setChanging(false);
                    }}
                  >
                    {[...new Set([zone, ...ZONES])].map((z) => (
                      <option key={z} value={z}>
                        {zoneLabel(z)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <button type="button" className={s.chg} onClick={() => setChanging(true)}>
                    Change
                  </button>
                )}
              </div>
              {v.provisional && (
                <div className={s.panelnote}>
                  <span className={s.g} />
                  <span>Another physician has accepted this time, so the panel is forming around it.</span>
                </div>
              )}
              <div className={s.days} role="radiogroup" aria-label="Offered times">
                {days.map((d) => (
                  <div key={d.day} className={s.day}>
                    <div className={s.dn}>{fmtDayLong(new Date(d.items[0]!), zone)}</div>
                    <div className={s.opts}>
                      {d.items.map((t) => (
                        <label key={t} className={`${s.opt} ${chosen === t ? s.on : ''}`}>
                          <input
                            type="radio"
                            name="time"
                            checked={chosen === t}
                            onChange={() => setPicked(t)}
                          />
                          <span className={s.r} />
                          {fmtTime(new Date(t), zone)}{' '}
                          {pt !== zone && (
                            <span className={s.et}>
                              {fmtTime(new Date(t), pt).replace(' AM', '').replace(' PM', '')}{' '}
                              {zoneAbbr(pt, new Date(t))}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className={s.decide}>
                {chosen && (
                  <div className={s.sel}>
                    You picked{' '}
                    <b>
                      {fmtDayLong(new Date(chosen), zone)} at {fmtTime(new Date(chosen), zone)}{' '}
                      {zoneLabel(zone).split(' Time')[0]}
                    </b>
                    . The time is confirmed once the panel fills or the navigator confirms it; you will get an
                    email either way, with the Zoom join link and a calendar file.
                  </div>
                )}
                {error && <Notice tone="bad">{errorMessage(error)}</Notice>}
                {declining ? (
                  <div className="field">
                    <label htmlFor="reason">Reason, if you want to give one</label>
                    <input
                      id="reason"
                      className="input"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Optional"
                    />
                    <div className={s.row}>
                      <Button variant="danger" busy={decline.isPending} onClick={() => decline.mutate()}>
                        Decline this case
                      </Button>
                      <Button variant="ghost" onClick={() => setDeclining(false)}>
                        Keep it open
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className={s.row}>
                    <Button
                      size="lg"
                      disabled={!chosen || busy}
                      busy={accept.isPending}
                      onClick={() => chosen && accept.mutate(chosen)}
                    >
                      {chosen ? `Accept ${fmtShort(chosen, zone)}` : 'Accept a time'}
                    </Button>
                    <Button variant="sec" size="lg" onClick={() => setDeclining(true)} disabled={busy}>
                      Decline
                    </Button>
                  </div>
                )}
                <p className={s.terms}>
                  By accepting you acknowledge the consultation terms: {v.terms.charAt(0).toLowerCase()}
                  {v.terms.slice(1)} The terms are restated in your confirmation email.
                </p>
              </div>
              {!v.provisional && (
                <div className={s.alt}>
                  <div className={s.h}>None of these work?</div>
                  <div className={s.d}>
                    Tell {v.navigatorName.split(' ')[0]} when you could do it. The case goes back to them with
                    your constraint attached and they will come back to you.
                  </div>
                  <textarea
                    className="textarea"
                    style={{ minHeight: 70 }}
                    aria-label="When you could do it"
                    placeholder="For example: any weekday after 4 PM Central, or Monday August 17 all day"
                    value={alt}
                    onChange={(e) => setAlt(e.target.value)}
                  />
                  <Button
                    variant="sec"
                    style={{ alignSelf: 'flex-start' }}
                    disabled={alt.trim().length < 3 || busy}
                    onClick={() => alternative.mutate()}
                  >
                    Request an alternative
                  </Button>
                </div>
              )}
            </Panel>
          ) : (
            <Outcome v={v} zone={zone} />
          )}

          <Panel aria-label="What happens next">
            <PanelHead title="What happens if the panel fills first" />
            <div className="pbody" style={{ fontSize: '.84rem', color: 'var(--ink-2)', lineHeight: 1.55 }}>
              If {size} {size === 1 ? 'physician accepts' : 'physicians accept'} before you respond, this
              invitation closes and you will receive a short note saying so. It is not a decline on your part
              and is not recorded as one.
            </div>
          </Panel>
        </div>
      </div>
    </PublicShell>
  );
}

function fmtShort(iso: string, tz: string) {
  const d = new Date(iso);
  return `${new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: tz }).format(d)} ${fmtTime(d, tz)}`;
}

function Outcome({ v, zone }: { v: View; zone: string }) {
  const text: Record<Exclude<View['state'], 'open'>, [string, string]> = {
    accepted: [
      'You accepted',
      v.provisional &&
      v.acceptedStartsAt &&
      Date.parse(v.provisional.startsAt) !== Date.parse(v.acceptedStartsAt)
        ? `The consultation has moved to ${fmtFull(new Date(v.provisional.startsAt), zone)}. You were emailed the new time with an updated calendar file.`
        : v.acceptedStartsAt
          ? `You accepted ${fmtFull(new Date(v.acceptedStartsAt), zone)}. You will get an email with the Zoom join link and a calendar file when the time is confirmed.`
          : 'You accepted. You will get an email when the time is confirmed.',
    ],
    declined: ['You declined', 'Thank you for letting us know. Nothing more is needed.'],
    alternative_requested: [
      'Your alternative was sent',
      `${v.navigatorName} has your constraint and will come back to you.`,
    ],
    missed_out: [
      'The panel is full',
      'The panel filled before you responded. This is not recorded as a decline. Thank you for your time.',
    ],
    expired: [
      'This invitation has closed',
      `The response window ended. If you can still help, contact ${v.navigatorName} at Cancer Expert Now.`,
    ],
    withdrawn: [
      'This invitation was withdrawn',
      'The care navigator withdrew this invitation. Nothing more is needed.',
    ],
    closed: ['This case is closed', 'The case has been closed or canceled. Nothing more is needed.'],
  };
  const [title, body] = text[v.state as Exclude<View['state'], 'open'>];
  return (
    <Panel aria-label="Your response">
      <PanelHead title={title} />
      <div className={s.state}>{body}</div>
    </Panel>
  );
}
